import { useEffect, useState } from 'react'
import Image from 'next/image'
import cn from 'classnames'
import { type Image as ApiImage } from '@mc/api'
import { Modal, ModalClose, ModalContent } from '@mc/design-system'
import {
  ExperimentVariable,
  StyledVariable,
  TEXT_KEY,
  URGENCY_TEXT_VARIABLE_NAME,
  UrgencyEntries,
  useUrgency,
} from '@mc/feature-variables'
import { useTranslation } from '@mc/i18n'
import { paths } from '@mc/paths'
import { useCertificateSlug, useModalLock } from 'pages/certificates/common'
import { Layout } from 'pages/certificates/common/Layout'
import { type PromoVariables } from 'pages/certificates/promo'
import {
  TrackClick,
  TrackingAttributes,
  TrackOnMount,
  TrackEvent,
} from 'pages/certificates/tracking'
import { SignUpButton } from '../SignUpButton'
import classes from './PromoModal.module.scss'

type PromoModalProps = {
  promo: PromoVariables['promo']
  modal: NonNullable<PromoVariables['modal']>
  certThumbnail?: ApiImage
  releaseLabel?: string
  checkoutHref?: string
  ctaText?: string
  ctaAction?: string
}

const MODAL_URGENCY_CONFIG = {
  modalUrgencyText: {
    variableName: URGENCY_TEXT_VARIABLE_NAME,
    variableKey: TEXT_KEY,
  },
} as UrgencyEntries

const SHOW_DELAY = 6000
const DISMISSED_KEY_BASE = 'certificate-promo-modal-dismissed'

const getDismissalKey = (slug: string, couponCode: string) =>
  `${DISMISSED_KEY_BASE}-${slug}-${couponCode}`

export const PromoModal = ({
  promo,
  modal,
  certThumbnail,
  releaseLabel,
  checkoutHref,
  ctaText,
  ctaAction,
}: PromoModalProps) => {
  const { t } = useTranslation('@mc/certificates')
  const { certSlug } = useCertificateSlug()
  const [show, setShow] = useState(false)
  const { isLocked } = useModalLock()
  const urgencies = useUrgency({
    entries: MODAL_URGENCY_CONFIG,
    variables: modal as unknown as ExperimentVariable,
  })

  const dismissedKey = getDismissalKey(certSlug, promo.productCoupon.default)

  // Delay modal by 6 seconds
  useEffect(() => {
    const hasClosedModal = localStorage.getItem(dismissedKey)
    if (hasClosedModal) {
      return () => {}
    }

    const timer = setTimeout(() => setShow(true), SHOW_DELAY)
    return () => {
      clearTimeout(timer)
    }
  }, [dismissedKey])

  const dismissModal = () => {
    localStorage.setItem(dismissedKey, 'true')
    setShow(false)
  }

  const handleClose = () => {
    dismissModal()
  }

  const shouldShow = show && !isLocked

  // `image_16x9` is the fallback when the payload opts in but the cert's
  // marketing content has no `thumbnail_16x9` attachment.
  const imageUrl =
    modal.use_cert_thumbnail && certThumbnail?.url
      ? certThumbnail.url
      : modal.image_16x9

  return (
    <TrackingAttributes
      location='promo modal'
      modal_type={promo.name ? `promo::${promo.name}` : promo}
    >
      <TrackEvent event='promoModalClicked' action='cancel' trigger='onClose'>
        <Modal show={shouldShow} onClose={handleClose} noInitialFocus>
          <TrackOnMount event='promoModalViewed' />
          <Layout>
            <ModalContent
              className={cn(
                'c-bg-base-600 mc-corners--lg mc-overflow--hidden',
                classes.modalContent,
              )}
            >
              <ModalClose
                className='c-text-color--light'
                style={{ opacity: 1, backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
              />
              <div
                style={{
                  width: '100%',
                  aspectRatio: 16 / 9,
                  position: 'relative',
                }}
              >
                <Image src={imageUrl} alt='' fill priority />
              </div>
              <div className='c-p-7 mc-text--center d-flex flex-column align-items-center'>
                {urgencies.modalUrgencyText && (
                  <p className='c-bg-brand-highlight c-text-color--dark mc-corners--lg c-px-3 c-py-1 c-mb-4 mc-w-fit-content c-caption-mono'>
                    <StyledVariable text={urgencies.modalUrgencyText} />
                  </p>
                )}
                <h2 className='c-heading-2 c-text-ivar-display-condensed c-mb-4'>
                  <StyledVariable text={modal.heading} />
                </h2>
                <p className='mc-text-large c-mb-5'>
                  <StyledVariable text={modal.body} />
                </p>
                <TrackClick event='promoModalClicked'>
                  <a
                    className='d-block mc-text--link c-mb-5 c-text-color--tint-3'
                    href={promo.terms || paths.terms}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {t('marketingPages.promo.termsLink')}
                  </a>
                </TrackClick>
                <TrackClick event='promoModalClicked' action='signup'>
                  <SignUpButton
                    arrow
                    onClick={dismissModal}
                    href={checkoutHref}
                    text={ctaText}
                    action={ctaAction}
                  />
                </TrackClick>
                {releaseLabel && (
                  <p className='c-mt-5 mc-text-small c-text-sohne-mono c-text-color--tint-3 mc-text--uppercase'>
                    {t('marketingPages.promo.courseAvailableWithLabel', {
                      releaseLabel,
                    })}
                  </p>
                )}
              </div>
            </ModalContent>
          </Layout>
        </Modal>
      </TrackEvent>
    </TrackingAttributes>
  )
}
